import React from 'react'

interface LocalSEOProps {
  pageName?: string
  serviceType?: string
  location?: string
}

const LocalSEO: React.FC<LocalSEOProps> = ({
  pageName = 'Home',
  serviceType = 'Video Production & Event Management',
  location = 'Ahmedabad'
}) => {
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://parthvideo.in/#localbusiness",
    "name": "Parth Video",
    "alternateName": "Parth Video Production",
    "description": `Professional ${serviceType} services in ${location}, Gujarat. Serving clients since 1999 with expertise in corporate events, weddings, photography, and live streaming.`,
    "url": "https://parthvideo.in",
    "telephone": "+91-7874467874",
    "email": "bhavesh7874@gmail.com",
    "foundingDate": "1999",
    "logo": "https://parthvideo.in/logo.png",
    "image": "https://parthvideo.in/og-image.jpg",
    "priceRange": "₹₹₹",
    "currenciesAccepted": "INR",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer, UPI",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": location,
      "addressLocality": location,
      "addressRegion": "Gujarat",
      "postalCode": "380001",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "23.0225",
      "longitude": "72.5714"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": location,
        "containedInPlace": {
          "@type": "State",
          "name": "Gujarat"
        }
      },
      {
        "@type": "State",
        "name": "Gujarat"
      },
      {
        "@type": "Place",
        "name": "Gandhinagar"
      },
      {
        "@type": "Place",
        "name": "Surat"
      },
      {
        "@type": "Place",
        "name": "Vadodara"
      }
    ],
    "serviceType": [
      "Video Production",
      "Event Management",
      "Wedding Videography",
      "Corporate Video Production",
      "Photography Services",
      "Live Streaming",
      "Event Planning",
      "Commercial Video Production",
      "Documentary Production",
      "Product Photography"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${serviceType} Services in ${location}`,
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `Corporate Video Production ${location}`,
            "description": `Professional corporate video production services in ${location}, Gujarat`,
            "areaServed": location
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `Wedding Videography ${location}`,
            "description": `Complete wedding video coverage and photography in ${location}, Gujarat`,
            "areaServed": location
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `Event Management ${location}`,
            "description": `Full-service event planning and management in ${location}, Gujarat`,
            "areaServed": location
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": `Photography Services ${location}`,
            "description": `Professional photography services for events and corporate needs in ${location}`,
            "areaServed": location
          }
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "10:00",
        "closes": "16:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/parthvideo",
      "https://www.instagram.com/parthvideo",
      "https://www.youtube.com/parthvideo",
      "https://www.linkedin.com/company/parthvideo"
    ],
    "knowsAbout": [
      `Video Production ${location}`,
      `Event Management ${location}`,
      `Wedding Photography ${location}`,
      `Corporate Events ${location}`,
      `Live Streaming ${location}`,
      "Commercial Video Gujarat",
      `Event Planning ${location}`,
      `Professional Photography Gujarat`,
      "Video Editing Services",
      "Documentary Production"
    ]
  }

  React.useEffect(() => {
    // Add local business schema
    let localSchemaScript = document.querySelector('script[data-local-seo="local-business"]')
    if (!localSchemaScript) {
      localSchemaScript = document.createElement('script')
      localSchemaScript.setAttribute('type', 'application/ld+json')
      localSchemaScript.setAttribute('data-local-seo', 'local-business')
      document.head.appendChild(localSchemaScript)
    }
    localSchemaScript.textContent = JSON.stringify(localBusinessSchema)

    // Add local keywords meta tag
    let localKeywords = document.querySelector('meta[name="local-keywords"]')
    if (!localKeywords) {
      localKeywords = document.createElement('meta')
      localKeywords.setAttribute('name', 'local-keywords')
      document.head.appendChild(localKeywords)
    }
    localKeywords.setAttribute('content', 
      `${serviceType.toLowerCase()} ${location.toLowerCase()}, ` +
      `video production ${location.toLowerCase()}, ` +
      `event management ${location.toLowerCase()}, ` +
      `photography services ${location.toLowerCase()}, ` +
      `wedding videography ${location.toLowerCase()}, ` +
      `corporate events ${location.toLowerCase()}, ` +
      `live streaming ${location.toLowerCase()}, ` +
      `professional photographer ${location.toLowerCase()}, ` +
      `video production company gujarat, ` +
      `best event management ${location.toLowerCase()}`
    )

    // Add geo meta tags
    let geoRegion = document.querySelector('meta[name="geo.region"]')
    if (!geoRegion) {
      geoRegion = document.createElement('meta')
      geoRegion.setAttribute('name', 'geo.region')
      document.head.appendChild(geoRegion)
    }
    geoRegion.setAttribute('content', 'IN-GJ')

    let geoPlacename = document.querySelector('meta[name="geo.placename"]')
    if (!geoPlacename) {
      geoPlacename = document.createElement('meta')
      geoPlacename.setAttribute('name', 'geo.placename')
      document.head.appendChild(geoPlacename)
    }
    geoPlacename.setAttribute('content', `${location}, Gujarat, India`)

    let geoPosition = document.querySelector('meta[name="geo.position"]')
    if (!geoPosition) {
      geoPosition = document.createElement('meta')
      geoPosition.setAttribute('name', 'geo.position')
      document.head.appendChild(geoPosition)
    }
    geoPosition.setAttribute('content', '23.0225;72.5714')

    let icbm = document.querySelector('meta[name="ICBM"]')
    if (!icbm) {
      icbm = document.createElement('meta')
      icbm.setAttribute('name', 'ICBM')
      document.head.appendChild(icbm)
    }
    icbm.setAttribute('content', '23.0225, 72.5714')

    return () => {
      // Cleanup function to remove elements when component unmounts
      const elementsToRemove = [
        'script[data-local-seo="local-business"]',
        'meta[name="local-keywords"]',
        'meta[name="geo.region"]',
        'meta[name="geo.placename"]',
        'meta[name="geo.position"]',
        'meta[name="ICBM"]'
      ]
      
      elementsToRemove.forEach(selector => {
        const element = document.querySelector(selector)
        if (element) {
          element.remove()
        }
      })
    }
  }, [pageName, serviceType, location])

  return null
}

export default LocalSEO